import {
  Popover as HUIPopover,
  PopoverButton as HUIPopoverButton,
  PopoverButtonProps as HUIPopoverButtonProps,
  PopoverPanel as HUIPopoverPanel,
} from '@headlessui/react';
import { ReactNode, useRef, ReactElement } from 'react';
import type { ElementType, HTMLAttributes } from 'react';
import {
  useFloating,
  arrow as arrowMiddleware,
  flip,
  shift,
  type Placement,
} from '@floating-ui/react';
import { Arrow, ArrowPositioner, HUIPopoverWrapper } from './Popover.styles';
import { ContentWrapper } from './ContentWrapper';

type ButtonSize = 'small' | 'large';

type ButtonCommonProps = {
  icon?: ReactElement;
  iconAlignment?: 'left' | 'right';
  size?: ButtonSize;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
  loading?: boolean;
  disabled?: boolean;
  textId?: string;
};

export interface PopoverProps extends HTMLAttributes<HTMLElement> {
  button: HUIPopoverButtonProps<ElementType> &
    ButtonCommonProps & {
      'data-testid'?: string;
    };
  position?: 'top' | 'bottom' | 'left' | 'right';
  children?: ReactNode;
}

const Popover = ({ button, position = 'bottom', children }: PopoverProps) => {
  const arrowRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, middlewareData } = useFloating({
    placement: position as Placement,
    strategy: 'absolute',
    middleware: [flip(), shift(), arrowMiddleware({ element: arrowRef })],
  });

  return (
    <HUIPopoverWrapper position={position}>
      <HUIPopover>
        <HUIPopoverButton
          {...button}
          role={button.as !== 'button' ? 'button' : undefined}
          ref={refs.setReference as React.Ref<HTMLButtonElement>}
        />
        <HUIPopoverPanel
          ref={refs.setFloating as React.Ref<HTMLDivElement>}
          style={floatingStyles}
        >
          <ContentWrapper>{children}</ContentWrapper>
          <ArrowPositioner
            ref={arrowRef}
            position={position}
            style={{
              left:
                middlewareData.arrow?.x != null
                  ? `${middlewareData.arrow.x}px`
                  : '',
              top:
                middlewareData.arrow?.y != null
                  ? `${middlewareData.arrow.y}px`
                  : '',
            }}
          >
            <Arrow position={position} />
          </ArrowPositioner>
        </HUIPopoverPanel>
      </HUIPopover>
    </HUIPopoverWrapper>
  );
};

export { Popover };
